import { ApolloError } from '@apollo/client';
import { Coordinate } from '@usepepper/ui/molecules/map/types';
import {
  ProspectDetailAddress,
  ProspectDetailRestaurant,
} from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/types';
import { useGeneratedSalesPortalProspectDetailQuery } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/documents/sales-portal-prospect-detail.generated';
import { useMemo } from 'react';

interface ProspectDetailLoaderParams {
  restaurantUUID: string;
  skip?: boolean;
  supplierUUID: string;
}

interface ProspectDetailLoaderResult {
  accountId: string | null;
  address: ProspectDetailAddress | null;
  chatUUID: string | null;
  coordinate?: Coordinate;
  error: ApolloError | undefined;
  loading: boolean;
  refetch: () => Promise<unknown>;
  restaurant: ProspectDetailRestaurant | undefined;
}

/**
 * Gets the core prospect record for the detail header.
 *
 * @param params.restaurantUUID - The UUID of the restaurant.
 * @param params.skip - Skips the query when true.
 * @param params.supplierUUID - The UUID of the supplier.
 * @returns The account code, the address, the chat UUID, the coordinate, the
 *   restaurant, a loading flag, an error and a refetch. Each value is null or undefined until
 *   the query gives a result.
 */
export const useProspectDetailLoader = ({
  restaurantUUID,
  skip,
  supplierUUID,
}: ProspectDetailLoaderParams): ProspectDetailLoaderResult => {
  const { data, error, loading, refetch } =
    useGeneratedSalesPortalProspectDetailQuery({
      notifyOnNetworkStatusChange: true,
      skip,
      variables: { restaurantUUID, supplierUUID },
    });

  const queryRestaurant = data?.restaurant;
  const queryAddress = queryRestaurant?.address;

  const latitude = queryAddress?.latitude;
  const longitude = queryAddress?.longitude;

  const coordinate = useMemo(
    () =>
      latitude === undefined || longitude === undefined
        ? undefined
        : { latitude, longitude },
    [latitude, longitude],
  );

  return {
    accountId:
      queryRestaurant?.restaurant_chats[0]?.restaurant_account_id ?? null,
    address: queryAddress
      ? {
          address: queryAddress.address,
          city: queryAddress.city,
          region: queryAddress.region,
        }
      : null,
    chatUUID: queryRestaurant?.restaurant_chats[0]?.uuid ?? null,
    coordinate,
    error,
    loading,
    refetch: () => refetch().catch(() => {}),
    restaurant: queryRestaurant
      ? { name: queryRestaurant.name, uuid: queryRestaurant.uuid }
      : undefined,
  };
};
